import * as React from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { Header } from '@/components/layout/header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  ArrowLeft,
  FileText,
  Users,
  Eye,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Send,
  XCircle,
  Upload,
  ImageIcon,
  X,
  Clock,
  CalendarClock,
  Plus,
  PlayCircle,
  Video,
} from 'lucide-react'
import { CampaignMessagePreview } from '@/components/whatsapp/campaign-message-preview'
import { AudienceSelector } from '@/components/whatsapp/audience-selector'
import { useWATemplates } from '@/hooks/useWATemplates'
import {
  useCreateWACampaign,
  useUpdateWACampaign,
  usePreviewAudienceFilter,
  useExecuteWACampaign,
  useUploadCampaignMedia,
  useWACampaign,
} from '@/hooks/useWACampaigns'
import { useWAAudiences, useCreateWAAudience } from '@/hooks/useWAAudiences'
import type { WAAudienceFilter, WATemplate, WAAudience } from '@/lib/api-client'

export const Route = createFileRoute('/whatsapp-campaigns_/new')({
  component: NewCampaignPage,
  validateSearch: (search: Record<string, unknown>) => ({
    editId: search.editId ? Number(search.editId) : undefined,
  }),
})

type Step = 'info' | 'template' | 'audience' | 'review'

const STEPS: { id: Step; label: string; icon: React.ReactNode }[] = [
  { id: 'info', label: 'Info', icon: <FileText className='w-4 h-4' /> },
  { id: 'template', label: 'Template', icon: <FileText className='w-4 h-4' /> },
  { id: 'audience', label: 'Audience', icon: <Users className='w-4 h-4' /> },
  { id: 'review', label: 'Review', icon: <Eye className='w-4 h-4' /> },
]

function NewCampaignPage() {
  const navigate = useNavigate()
  const { editId } = Route.useSearch()
  const isEditMode = !!editId

  const [step, setStep] = React.useState<Step>('info')
  const [confirmExecuteOpen, setConfirmExecuteOpen] = React.useState(false)
  const [createdCampaignId, setCreatedCampaignId] = React.useState<number | null>(null)
  const [scheduleMode, setScheduleMode] = React.useState(false)
  const [scheduledDateTime, setScheduledDateTime] = React.useState('')
  const [initialized, setInitialized] = React.useState(!isEditMode)

  // Form state
  const [name, setName] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [selectedTemplate, setSelectedTemplate] = React.useState<WATemplate | null>(null)
  const [selectedAudience, setSelectedAudience] = React.useState<WAAudience | null>(null)
  const [mediaFile, setMediaFile] = React.useState<File | null>(null)
  const [mediaPreview, setMediaPreview] = React.useState<string | null>(null)
  const [useTemplateMedia, setUseTemplateMedia] = React.useState(false)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  // Load existing campaign for edit mode
  const { data: existingCampaign } = useWACampaign(editId ?? null)

  // Queries
  const { data: templatesData, isPending: templatesLoading } = useWATemplates({
    status: 'approved',
    limit: 50,
    sortBy: 'name',
    sortOrder: 'asc',
  })
  const templates = templatesData?.data || []

  // Fetch all audiences for picker
  const { data: audiencesData, isPending: audiencesLoading } = useWAAudiences({
    limit: 50,
    sortBy: 'name',
    sortOrder: 'asc',
  })
  const audiences = audiencesData?.data || []

  // Pre-fill form when editing an existing campaign
  React.useEffect(() => {
    if (!isEditMode || initialized || !existingCampaign) return
    if (!templatesData || !audiencesData) return

    setName(existingCampaign.name)
    setDescription(existingCampaign.description || '')

    // Match template
    const tmpl = templates.find(t => t.id === existingCampaign.templateId)
    if (tmpl) setSelectedTemplate(tmpl)

    // Match audience
    if (existingCampaign.audienceId) {
      const aud = audiences.find(a => a.id === existingCampaign.audienceId)
      if (aud) setSelectedAudience(aud)
    }

    // If campaign already has media uploaded, show template media as fallback
    if (tmpl?.headerType === 'image' && tmpl?.headerContent) {
      setUseTemplateMedia(true)
    }

    setInitialized(true)
  }, [isEditMode, initialized, existingCampaign, templatesData, audiencesData])

  // Mutations
  const createMutation = useCreateWACampaign()
  const updateMutation = useUpdateWACampaign()
  const executeMutation = useExecuteWACampaign()
  const previewMutation = usePreviewAudienceFilter()
  const uploadMediaMutation = useUploadCampaignMedia()
  const createAudienceMutation = useCreateWAAudience()

  // Quick Create Audience state
  const [quickCreateOpen, setQuickCreateOpen] = React.useState(false)
  const [quickAudienceName, setQuickAudienceName] = React.useState('')
  const [quickAudienceFilter, setQuickAudienceFilter] = React.useState<WAAudienceFilter>({})

  const handleQuickCreateAudience = () => {
    if (!quickAudienceName.trim()) return

    createAudienceMutation.mutate(
      {
        name: quickAudienceName.trim(),
        type: 'dynamic',
        filters: quickAudienceFilter,
      },
      {
        onSuccess: (newAudience) => {
          setSelectedAudience(newAudience)
          setQuickCreateOpen(false)
          setQuickAudienceName('')
          setQuickAudienceFilter({})
        },
      }
    )
  }

  const needsMediaUpload = selectedTemplate?.headerType === 'image' || selectedTemplate?.headerType === 'video'
  const isVideoTemplate = selectedTemplate?.headerType === 'video'
  const currentStepIndex = STEPS.findIndex((s) => s.id === step)

  const canProceed = () => {
    switch (step) {
      case 'info':
        return name.trim().length > 0
      case 'template':
        if (!selectedTemplate) return false
        if ((selectedTemplate.headerType === 'image' || selectedTemplate.headerType === 'video') && !mediaFile && !useTemplateMedia) return false
        return true
      case 'audience':
        return !!selectedAudience
      case 'review':
        return true
      default:
        return false
    }
  }

  const handleNext = () => {
    if (step === 'review') {
      handleCreate()
    } else {
      const nextIndex = currentStepIndex + 1
      if (nextIndex < STEPS.length) {
        setStep(STEPS[nextIndex]!.id)
      }
    }
  }

  const handleBack = () => {
    const prevIndex = currentStepIndex - 1
    if (prevIndex >= 0) {
      setStep(STEPS[prevIndex]!.id)
    }
  }

  const handleCreate = async () => {
    if (!selectedTemplate) return

    try {
      let campaignId: number

      if (isEditMode && editId) {
        // Update existing draft campaign
        const updated = await updateMutation.mutateAsync({
          id: editId,
          data: {
            name: name.trim(),
            description: description.trim() || undefined,
            templateId: selectedTemplate.id,
            audienceId: selectedAudience?.id || null,
            audienceFilter: selectedAudience?.type === 'dynamic' && selectedAudience?.filters
              ? selectedAudience.filters
              : undefined,
          },
        })
        campaignId = updated.id
      } else {
        // Create new campaign
        const campaign = await createMutation.mutateAsync({
          name: name.trim(),
          description: description.trim() || undefined,
          templateId: selectedTemplate.id,
          audienceId: selectedAudience?.id,
          audienceFilter: selectedAudience?.type === 'dynamic' && selectedAudience?.filters
            ? selectedAudience.filters
            : undefined,
        })
        campaignId = campaign.id
      }

      if (mediaFile && needsMediaUpload && !useTemplateMedia) {
        await uploadMediaMutation.mutateAsync({ id: campaignId, file: mediaFile })
      }

      setCreatedCampaignId(campaignId)
      setConfirmExecuteOpen(true)
    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} campaign:`, error)
    }
  }

  const handleExecute = async () => {
    if (!createdCampaignId) return

    try {
      await executeMutation.mutateAsync({ id: createdCampaignId })
      setConfirmExecuteOpen(false)
      navigate({ to: '/whatsapp-campaigns/$id', params: { id: String(createdCampaignId) } })
    } catch (error) {
      console.error('Failed to execute campaign:', error)
    }
  }

  const handleSchedule = async () => {
    if (!createdCampaignId || !scheduledDateTime) return

    try {
      // Convert local datetime to ISO string (browser local = MYT)
      const scheduledAt = new Date(scheduledDateTime).toISOString()
      await executeMutation.mutateAsync({ id: createdCampaignId, scheduledAt })
      setConfirmExecuteOpen(false)
      navigate({ to: '/whatsapp-campaigns/$id', params: { id: String(createdCampaignId) } })
    } catch (error) {
      console.error('Failed to schedule campaign:', error)
    }
  }

  const handleSaveAsDraft = () => {
    setConfirmExecuteOpen(false)
    navigate({ to: '/whatsapp-campaigns' })
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const isVideo = file.type.startsWith('video/')
    const allowedImages = ['image/jpeg', 'image/png', 'image/webp']
    const allowedVideos = ['video/mp4']

    if (!allowedImages.includes(file.type) && !allowedVideos.includes(file.type)) {
      alert(isVideoTemplate
        ? 'Invalid file type. Please select an MP4 video.'
        : 'Invalid file type. Please select JPEG, PNG, or WEBP.')
      return
    }

    const maxSize = isVideo ? 16 * 1024 * 1024 : 5 * 1024 * 1024
    if (file.size > maxSize) {
      alert(`File too large. Maximum size is ${isVideo ? '16' : '5'}MB.`)
      return
    }

    setMediaFile(file)
    setUseTemplateMedia(false)
    const url = URL.createObjectURL(file)
    setMediaPreview(url)
  }

  const handleRemoveMedia = () => {
    setMediaFile(null)
    if (mediaPreview) {
      URL.revokeObjectURL(mediaPreview)
      setMediaPreview(null)
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  React.useEffect(() => {
    handleRemoveMedia()
    if ((selectedTemplate?.headerType === 'image' || selectedTemplate?.headerType === 'video') && selectedTemplate?.headerContent) {
      setUseTemplateMedia(true)
    } else {
      setUseTemplateMedia(false)
    }
  }, [selectedTemplate?.id])

  // Get audience preview for review step
  const [audiencePreview, setAudiencePreview] = React.useState<{ totalCount: number } | null>(null)
  React.useEffect(() => {
    if (step === 'review' && selectedAudience) {
      if (selectedAudience.type === 'dynamic' && selectedAudience.filters) {
        previewMutation.mutate(selectedAudience.filters, {
          onSuccess: (data) => setAudiencePreview({ totalCount: data.totalCount }),
        })
      } else {
        setAudiencePreview({ totalCount: selectedAudience.estimatedCount || 0 })
      }
    }
  }, [step])

  const effectiveMediaPreview = mediaPreview
    || (useTemplateMedia && selectedTemplate?.headerContent ? selectedTemplate.headerContent : null)

  return (
    <div className="flex flex-col h-full">
      <Header
        title={isEditMode ? 'Edit Campaign' : 'Create Campaign'}
        subtitle={`Step ${currentStepIndex + 1} of ${STEPS.length}`}
      />

      <div className="flex-1 overflow-auto p-3 sm:p-4 lg:p-6">
        {/* Back button */}
        <Button
          variant="ghost"
          size="sm"
          className="mb-4 -ml-2 text-text-muted hover:text-text-dark"
          onClick={() => navigate({ to: '/whatsapp-campaigns' })}
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Campaigns
        </Button>

        {/* Progress Steps */}
        <div className="mb-6">
          <div className="flex items-center justify-between max-w-3xl mx-auto">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.id}>
                <button
                  type="button"
                  onClick={() => i < currentStepIndex && setStep(s.id)}
                  disabled={i > currentStepIndex}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-colors',
                    step === s.id
                      ? 'bg-primary text-primary-foreground'
                      : i < currentStepIndex
                        ? 'bg-success/10 text-success hover:bg-success/20 cursor-pointer'
                        : 'bg-muted text-text-muted'
                  )}
                >
                  {i < currentStepIndex ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    s.icon
                  )}
                  <span className="hidden sm:inline">{s.label}</span>
                </button>
                {i < STEPS.length - 1 && (
                  <div
                    className={cn(
                      'flex-1 h-0.5 mx-2',
                      i < currentStepIndex ? 'bg-success' : 'bg-border'
                    )}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content */}
        {step === 'info' && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left: Form */}
            <div className="lg:col-span-3 space-y-4 rounded-xl border border-border bg-card p-6">
              <div>
                <Label htmlFor="name" className="text-sm font-medium mb-2 block">
                  Campaign Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  placeholder="e.g. Holiday Promo 2026"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-10"
                />
              </div>
              <div>
                <Label htmlFor="description" className="text-sm font-medium mb-2 block">
                  Description
                </Label>
                <Textarea
                  id="description"
                  placeholder="Brief description about this campaign..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="min-h-[100px]"
                />
              </div>
            </div>

            {/* Right: Campaign progress summary */}
            <div className="lg:col-span-2 lg:sticky lg:top-6 self-start">
              <div className="rounded-xl border border-border bg-card p-6 space-y-4">
                <h3 className="text-sm font-medium text-text-dark">Campaign Progress</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      'w-6 h-6 rounded-full flex items-center justify-center text-xs',
                      name.trim() ? 'bg-success/10 text-success' : 'bg-muted text-text-muted'
                    )}>
                      {name.trim() ? <CheckCircle className="w-3.5 h-3.5" /> : '1'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-text-muted">Campaign Name</p>
                      <p className="text-sm font-medium text-text-dark truncate">
                        {name.trim() || 'Not set'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs bg-muted text-text-muted">
                      2
                    </div>
                    <div>
                      <p className="text-xs text-text-muted">Template</p>
                      <p className="text-sm text-text-muted">Not selected yet</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs bg-muted text-text-muted">
                      3
                    </div>
                    <div>
                      <p className="text-xs text-text-muted">Audience</p>
                      <p className="text-sm text-text-muted">Not configured yet</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 'template' && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left: Template selection */}
            <div className="lg:col-span-3 rounded-xl bg-card p-4 sm:p-6 space-y-4">
              <p className="text-sm text-text-muted">
                Select an approved template from Meta
              </p>
              {templatesLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} className="h-20 w-full" />
                  ))}
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-8">
                  <XCircle className="w-12 h-12 text-text-muted mx-auto mb-4" />
                  <p className="text-sm text-text-muted">No approved templates</p>
                  <p className="text-xs text-text-muted mt-1">
                    Please sync templates from Meta first
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {templates.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => setSelectedTemplate(template)}
                        className={cn(
                          'flex flex-col p-4 rounded-lg border text-left transition-all',
                          selectedTemplate?.id === template.id
                            ? 'border-primary bg-primary/5 ring-1 ring-primary'
                            : 'border-border hover:border-border/80 hover:bg-muted/30'
                        )}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <span className="font-medium text-text-dark text-sm">
                            {template.name}
                          </span>
                          <div className="flex items-center gap-1 shrink-0">
                            {template.headerType === 'image' && (
                              <Badge variant="outline" className="text-[10px] border-blue-500/30 text-blue-500">
                                <ImageIcon className="w-3 h-3 mr-1" />
                                Image
                              </Badge>
                            )}
                            {template.headerType === 'video' && (
                              <Badge variant="outline" className="text-[10px] border-purple-500/30 text-purple-500">
                                <PlayCircle className="w-3 h-3 mr-1" />
                                Video
                              </Badge>
                            )}
                            <Badge variant="outline" className="text-[10px]">
                              {template.language}
                            </Badge>
                          </div>
                        </div>
                        <p className="text-xs text-text-muted line-clamp-2">
                          {template.bodyContent}
                        </p>
                      </button>
                    ))}
                  </div>

                  {needsMediaUpload && selectedTemplate && (
                    <div className="mt-4 p-4 rounded-lg border border-dashed border-border bg-muted/20">
                      <div className="flex items-center gap-2 mb-3">
                        {isVideoTemplate ? (
                          <Video className="w-4 h-4 text-primary" />
                        ) : (
                          <ImageIcon className="w-4 h-4 text-primary" />
                        )}
                        <span className="text-sm font-medium text-text-dark">
                          Header {isVideoTemplate ? 'Video' : 'Image'}
                        </span>
                        {!mediaPreview && !useTemplateMedia && (
                          <span className="text-xs text-text-muted">
                            (required for this template)
                          </span>
                        )}
                      </div>

                      {mediaPreview ? (
                        <div className="relative inline-block">
                          {isVideoTemplate ? (
                            <video
                              src={mediaPreview}
                              className="w-48 h-32 object-cover rounded-lg border border-border"
                              muted
                              playsInline
                              onMouseEnter={(e) => (e.target as HTMLVideoElement).play()}
                              onMouseLeave={(e) => { const v = e.target as HTMLVideoElement; v.pause(); v.currentTime = 0 }}
                            />
                          ) : (
                            <img
                              src={mediaPreview}
                              alt="Campaign media"
                              className="w-48 h-32 object-cover rounded-lg border border-border"
                            />
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              handleRemoveMedia()
                              if (selectedTemplate?.headerContent) {
                                setUseTemplateMedia(true)
                              }
                            }}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center hover:bg-destructive/80 transition-colors"
                          >
                            <X className="w-3 h-3" />
                          </button>
                          <p className="text-xs text-text-muted mt-2">
                            {mediaFile?.name} ({((mediaFile?.size || 0) / (1024 * 1024)).toFixed(1)} MB)
                          </p>
                        </div>
                      ) : useTemplateMedia && selectedTemplate?.headerContent ? (
                        <div className="space-y-3">
                          <div className="relative inline-block">
                            {isVideoTemplate ? (
                              <div className="w-48 h-32 rounded-lg border border-border bg-muted flex items-center justify-center">
                                <PlayCircle className="w-10 h-10 text-text-muted" />
                              </div>
                            ) : (
                              <img
                                src={selectedTemplate.headerContent}
                                alt="Template header"
                                className="w-48 h-32 object-cover rounded-lg border border-border"
                              />
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge variant="secondary" className="text-xs">
                              {isVideoTemplate ? (
                                <Video className="w-3 h-3 mr-1" />
                              ) : (
                                <ImageIcon className="w-3 h-3 mr-1" />
                              )}
                              Using template {isVideoTemplate ? 'video' : 'image'}
                            </Badge>
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              className="text-xs text-primary hover:underline"
                            >
                              Change {isVideoTemplate ? 'video' : 'image'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="flex flex-col items-center justify-center w-full py-8 rounded-lg border border-dashed border-border hover:border-primary/50 hover:bg-primary/5 transition-colors cursor-pointer"
                        >
                          <Upload className="w-8 h-8 text-text-muted mb-2" />
                          <span className="text-sm text-text-muted">
                            Click to upload {isVideoTemplate ? 'video' : 'image'}
                          </span>
                          <span className="text-xs text-text-muted mt-1">
                            {isVideoTemplate ? 'MP4 (max 16MB)' : 'JPEG, PNG, or WEBP (max 5MB)'}
                          </span>
                        </button>
                      )}

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={isVideoTemplate ? 'video/mp4' : 'image/jpeg,image/png,image/webp'}
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Right: Sticky preview */}
            <div className="lg:col-span-2 lg:sticky lg:top-6 self-start">
              <CampaignMessagePreview
                template={selectedTemplate}
                mediaPreview={effectiveMediaPreview}
                isVideo={isVideoTemplate}
              />
            </div>
          </div>
        )}

        {step === 'audience' && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left: Audience picker */}
            <div className="lg:col-span-3 rounded-xl bg-card p-4 sm:p-6 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-text-muted">
                    Select a saved audience or create one
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setQuickCreateOpen(true)}
                    className="h-8"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1.5" />
                    Quick Create
                  </Button>
                </div>

                {audiencesLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : audiences.length === 0 ? (
                  <div className="text-center py-8 border border-dashed border-border rounded-lg">
                    <Users className="w-10 h-10 text-text-muted mx-auto mb-3" />
                    <p className="text-sm text-text-muted mb-2">No audiences created yet</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setQuickCreateOpen(true)}
                      className="mt-2"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1.5" />
                      Create Your First Audience
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {audiences.map((audience) => {
                      const isSelected = selectedAudience?.id === audience.id
                      const typeLabel = audience.type === 'dynamic' ? 'Dynamic'
                        : audience.type === 'static' ? 'Static'
                        : 'Manual'

                      return (
                        <button
                          key={audience.id}
                          type="button"
                          onClick={() => setSelectedAudience(audience)}
                          className={cn(
                            'flex items-center justify-between w-full p-4 rounded-lg border text-left transition-all',
                            isSelected
                              ? 'border-primary bg-primary/5 ring-1 ring-primary'
                              : 'border-border hover:border-primary/30 hover:bg-muted/30'
                          )}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            {isSelected ? (
                              <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                            ) : (
                              <Users className="w-5 h-5 text-text-muted flex-shrink-0" />
                            )}
                            <div className="min-w-0">
                              <p className="font-medium text-sm text-text-dark truncate">
                                {audience.name}
                              </p>
                              {audience.description && (
                                <p className="text-xs text-text-muted truncate mt-0.5">
                                  {audience.description}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                            <Badge variant="outline" className="text-[10px]">
                              {typeLabel}
                            </Badge>
                            <span className="text-sm font-medium text-text-dark">
                              {(audience.estimatedCount || 0).toLocaleString()}
                            </span>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Right: Sticky sidebar with preview */}
            <div className="lg:col-span-2 lg:sticky lg:top-6 self-start space-y-4">
              <CampaignMessagePreview
                template={selectedTemplate}
                mediaPreview={effectiveMediaPreview}
                isVideo={isVideoTemplate}
                emptyMessage="Template preview will appear here"
              />

              {selectedAudience && (
                <div className="p-4 rounded-xl border border-border bg-card">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-primary" />
                    <p className="text-sm font-medium text-text-dark">{selectedAudience.name}</p>
                  </div>
                  <p className="text-xs text-text-muted mb-2">
                    {selectedAudience.type === 'dynamic' ? 'Filters re-evaluated at send time' : `${selectedAudience.estimatedCount} frozen contacts`}
                  </p>
                  {selectedAudience.type === 'dynamic' && selectedAudience.filters && (
                    <div className="flex flex-wrap gap-1.5">
                      {selectedAudience.filters.branchIds && selectedAudience.filters.branchIds.length > 0 && (
                        <Badge variant="secondary" className="text-[10px]">
                          {selectedAudience.filters.branchIds.length} branches
                        </Badge>
                      )}
                      {selectedAudience.filters.inactiveDays && (
                        <Badge variant="secondary" className="text-[10px]">
                          {selectedAudience.filters.inactiveDays}+ days inactive
                        </Badge>
                      )}
                      {selectedAudience.filters.gender && (
                        <Badge variant="secondary" className="text-[10px]">
                          {selectedAudience.filters.gender}
                        </Badge>
                      )}
                      {selectedAudience.filters.minTotalSpent && (
                        <Badge variant="secondary" className="text-[10px]">
                          RM{selectedAudience.filters.minTotalSpent}+
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {step === 'review' && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left: Campaign summary */}
            <div className="lg:col-span-3 rounded-xl bg-card p-4 sm:p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-muted/30">
                  <p className="text-xs text-text-muted mb-1">Campaign Name</p>
                  <p className="font-medium text-text-dark">{name}</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/30">
                  <p className="text-xs text-text-muted mb-1">Template</p>
                  <p className="font-medium text-text-dark">
                    {selectedTemplate?.name || '-'}
                  </p>
                </div>
              </div>

              {description && (
                <div className="p-4 rounded-lg bg-muted/30">
                  <p className="text-xs text-text-muted mb-1">Description</p>
                  <p className="text-sm text-text-dark">{description}</p>
                </div>
              )}

              <div className="p-5 rounded-xl bg-primary/5 border border-primary/20">
                <div className="flex items-center gap-3">
                  <Users className="w-10 h-10 text-primary" />
                  <div>
                    <p className="text-xs text-text-muted">Total Recipients</p>
                    {previewMutation.isPending ? (
                      <Skeleton className="h-8 w-24 mt-1" />
                    ) : (
                      <p className="text-3xl font-semibold text-text-dark">
                        {audiencePreview?.totalCount.toLocaleString() || '0'}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {selectedAudience && (
                <div className="p-4 rounded-lg bg-muted/30">
                  <p className="text-xs text-text-muted mb-1">Audience</p>
                  <p className="font-medium text-text-dark">{selectedAudience.name}</p>
                  <p className="text-xs text-text-muted mt-1 capitalize">{selectedAudience.type} audience</p>
                  {selectedAudience.type === 'dynamic' && selectedAudience.filters && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {selectedAudience.filters.branchIds && selectedAudience.filters.branchIds.length > 0 && (
                        <Badge variant="secondary" className="text-[10px]">
                          {selectedAudience.filters.branchIds.length} branches
                        </Badge>
                      )}
                      {selectedAudience.filters.inactiveDays && (
                        <Badge variant="secondary" className="text-[10px]">
                          {selectedAudience.filters.inactiveDays}+ days inactive
                        </Badge>
                      )}
                      {selectedAudience.filters.gender && (
                        <Badge variant="secondary" className="text-[10px]">
                          {selectedAudience.filters.gender}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right: Sticky preview */}
            <div className="lg:col-span-2 lg:sticky lg:top-6 self-start">
              <CampaignMessagePreview
                template={selectedTemplate}
                mediaPreview={effectiveMediaPreview}
                isVideo={isVideoTemplate}
              />
            </div>
          </div>
        )}

        {/* Footer Navigation */}
        <div className="mt-6 flex items-center justify-between pt-4 border-t border-border">
          <Button
            variant="ghost"
            onClick={currentStepIndex > 0 ? handleBack : () => navigate({ to: '/whatsapp-campaigns' })}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {currentStepIndex > 0 ? 'Back' : 'Cancel'}
          </Button>
          <Button
            onClick={handleNext}
            disabled={!canProceed() || createMutation.isPending || updateMutation.isPending || uploadMediaMutation.isPending}
          >
            {(createMutation.isPending || updateMutation.isPending || uploadMediaMutation.isPending) && (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            )}
            {uploadMediaMutation.isPending
              ? `Uploading ${isVideoTemplate ? 'Video' : 'Image'}...`
              : step === 'review'
                ? isEditMode ? 'Update Campaign' : 'Create Campaign'
                : 'Next'}
            {step !== 'review' && <ChevronRight className='w-4 h-4 ml-1' />}
          </Button>
        </div>
      </div>

      {/* Execute / Schedule Confirmation */}
      <AlertDialog open={confirmExecuteOpen} onOpenChange={(open) => {
        setConfirmExecuteOpen(open)
        if (!open) {
          setScheduleMode(false)
          setScheduledDateTime('')
        }
      }}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {scheduleMode ? 'Schedule Campaign' : 'Send Campaign'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Campaign "{name}" has been created with{" "}
              <strong>{audiencePreview?.totalCount.toLocaleString() || 0}</strong> recipients.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {scheduleMode ? (
            <div className="space-y-3 py-2">
              <Label htmlFor="schedule-datetime" className="text-sm font-medium">
                Schedule Date & Time (MYT)
              </Label>
              <Input
                id="schedule-datetime"
                type="datetime-local"
                value={scheduledDateTime}
                onChange={(e) => setScheduledDateTime(e.target.value)}
                min={new Date(Date.now() + 5 * 60_000).toISOString().slice(0, 16)}
                className="h-10"
              />
              {scheduledDateTime && (
                <p className="text-xs text-text-muted">
                  Will be sent on{' '}
                  {new Date(scheduledDateTime).toLocaleString('en-MY', {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                    timeZone: 'Asia/Kuala_Lumpur',
                  })}
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-text-muted py-2">
              Choose to send now or schedule for later.
            </p>
          )}

          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={handleSaveAsDraft}>
              Save as Draft
            </AlertDialogCancel>

            {scheduleMode ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setScheduleMode(false)
                    setScheduledDateTime('')
                  }}
                >
                  Back
                </Button>
                <Button
                  onClick={handleSchedule}
                  disabled={executeMutation.isPending || !scheduledDateTime}
                >
                  {executeMutation.isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CalendarClock className="w-4 h-4 mr-2" />
                  )}
                  Confirm Schedule
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => setScheduleMode(true)}
                >
                  <Clock className="w-4 h-4 mr-2" />
                  Schedule
                </Button>
                <Button
                  onClick={handleExecute}
                  disabled={executeMutation.isPending}
                >
                  {executeMutation.isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send Now
                </Button>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Quick Create Audience Dialog */}
      <Dialog open={quickCreateOpen} onOpenChange={(open) => {
        setQuickCreateOpen(open)
        if (!open) {
          setQuickAudienceName('')
          setQuickAudienceFilter({})
        }
      }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Quick Create Audience</DialogTitle>
            <DialogDescription>
              Create a dynamic audience with filters. You can refine it later from the Audiences page.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="quick-audience-name" className="text-sm font-medium mb-2 block">
                Audience Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="quick-audience-name"
                placeholder="e.g. Inactive 90 days - Shah Alam"
                value={quickAudienceName}
                onChange={(e) => setQuickAudienceName(e.target.value)}
                className="h-10"
              />
            </div>

            <AudienceSelector
              value={quickAudienceFilter}
              onChange={setQuickAudienceFilter}
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setQuickCreateOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleQuickCreateAudience}
              disabled={!quickAudienceName.trim() || createAudienceMutation.isPending}
            >
              {createAudienceMutation.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Create & Select
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
